import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';

import { CAMPAIGN_LABEL_TYPE } from '../constant';
import type {
  CampaignDraftCreatorItem,
  CampaignDraftPostItem,
  ImportError,
  ImportSummary,
} from '../type';
import {
  CAMPAIGN_DEFAULT_PLATFORM,
  CAMPAIGN_SUPPORTED_PLATFORMS,
  CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
  buildCampaignTrackPostItem,
  buildCreatorDedupKey,
  buildPostDedupKey,
  isCampaignSupportedPlatform,
  validateCreatorInput,
} from './tracking-source';

const CREATOR_HEADERS = {
  source: ['source', 'usernameorprofilelink', 'usernameorlink', 'account', 'accountinput'],
  username: ['username', 'handle', 'creatorusername'],
  platform: ['platform', 'channel'],
  profileLink: ['profilelink', 'profileurl', 'link', 'url'],
  label: ['label', 'tag'],
};

const POST_HEADERS = {
  postUrl: ['posturl', 'url', 'link'],
};

const INVALID_CREATOR_INPUT_MESSAGE =
  'Use a creator handle or a supported profile URL.';

function normalizeText(value: string) {
  return value.trim();
}

function normalizeHeader(value: string) {
  return value.toLowerCase().replace(/[^a-z]/g, '');
}

function parseCsvLine(line: string) {
  const cells: string[] = [];
  let current = '';
  let isQuoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];

    if (character === '"') {
      if (isQuoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        isQuoted = !isQuoted;
      }
      continue;
    }

    if (character === ',' && !isQuoted) {
      cells.push(current.trim());
      current = '';
      continue;
    }

    current += character;
  }

  cells.push(current.trim());
  return cells;
}

function parseCsvRows(text: string) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => parseCsvLine(line));
}

function detectHeaderIndexMap(row: string[], headerAliases: Record<string, string[]>) {
  const indexMap = new Map<string, number>();

  row.forEach((value, index) => {
    const normalizedValue = normalizeHeader(value);
    Object.entries(headerAliases).forEach(([key, aliases]) => {
      if (aliases.includes(normalizedValue)) {
        indexMap.set(key, index);
      }
    });
  });

  return indexMap;
}

function hasKnownHeaders(indexMap: Map<string, number>) {
  return indexMap.size > 0;
}

function getCell(row: string[], indexMap: Map<string, number>, key: string) {
  const index = indexMap.get(key);
  if (index == null) return '';
  return normalizeText(row[index] ?? '');
}

type CreatorImportResult = {
  rows: CampaignDraftCreatorItem[];
  summary: ImportSummary;
};

type PostImportResult = {
  rows: CampaignDraftPostItem[];
  summary: ImportSummary;
};

function createImportSummary(
  kind: 'creator' | 'post',
  fileName: string,
  added: number,
  duplicates: number,
  errors: ImportError[],
): ImportSummary {
  return {
    kind,
    fileName,
    added,
    duplicates,
    errors,
  };
}

export function importCreatorsFromCsv(args: {
  text: string;
  fileName: string;
  existingRows: CampaignDraftCreatorItem[];
  availableSlots: number;
  itemLimit: number;
}): CreatorImportResult {
  const csvRows = parseCsvRows(args.text);
  if (csvRows.length === 0) {
    return {
      rows: args.existingRows,
      summary: createImportSummary('creator', args.fileName, 0, 0, [
        { row: 1, message: 'CSV is empty.' },
      ]),
    };
  }

  const [firstRow, ...remainingRows] = csvRows;
  const headerIndexMap = detectHeaderIndexMap(firstRow, CREATOR_HEADERS);
  const hasHeaders = hasKnownHeaders(headerIndexMap);
  const dataRows = hasHeaders ? remainingRows : csvRows;
  const errors: ImportError[] = [];
  const seenKeys = new Set(
    args.existingRows.map((item) => buildCreatorDedupKey(item)).filter(Boolean),
  );
  const importedRows = [...args.existingRows];
  let duplicates = 0;
  let added = 0;

  dataRows.forEach((row, rowIndex) => {
    const rowNumber = hasHeaders ? rowIndex + 2 : rowIndex + 1;
    const sourceValue = hasHeaders
      ? getCell(row, headerIndexMap, 'source') ||
        getCell(row, headerIndexMap, 'profileLink') ||
        getCell(row, headerIndexMap, 'username')
      : normalizeText(row[0] ?? '');
    const platform = (
      hasHeaders ? getCell(row, headerIndexMap, 'platform') : normalizeText(row[1] ?? '')
    ).toLowerCase() as SOURCE_PLATFORM_TYPE | '';
    const parseLabel = (): CAMPAIGN_LABEL_TYPE | undefined => {
      const text = hasHeaders ? getCell(row, headerIndexMap, 'label') : normalizeText(row[2]);
      const matchedLabel = Object.values(CAMPAIGN_LABEL_TYPE).find(
        (labelType) => labelType === text,
      );
      return matchedLabel;
    };

    const { classification, error } = validateCreatorInput(sourceValue);
    if (!classification || error) {
      errors.push({
        row: rowNumber,
        message: error ?? INVALID_CREATOR_INPUT_MESSAGE,
      });
      return;
    }

    if (platform && !Object.values(SOURCE_PLATFORM_TYPE).includes(platform)) {
      errors.push({ row: rowNumber, message: 'Use instagram, tiktok, or youtube for platform.' });
      return;
    }

    const nextPlatform = classification.detectedPlatform ?? (platform || CAMPAIGN_DEFAULT_PLATFORM);
    if (!isCampaignSupportedPlatform(nextPlatform)) {
      errors.push({
        row: rowNumber,
        message: CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
      });
      return;
    }

    const nextItem: CampaignDraftCreatorItem = {
      id: crypto.randomUUID(),
      sourceValue: classification.normalizedValue,
      platform: nextPlatform,
      label: parseLabel(),
    };

    const dedupKey = buildCreatorDedupKey(nextItem);
    if (!dedupKey) {
      errors.push({ row: rowNumber, message: INVALID_CREATOR_INPUT_MESSAGE });
      return;
    }

    if (seenKeys.has(dedupKey)) {
      duplicates += 1;
      return;
    }

    if (added >= args.availableSlots) {
      errors.push({
        row: rowNumber,
        message: `This row exceeds the ${args.itemLimit} item campaign limit.`,
      });
      return;
    }

    seenKeys.add(dedupKey);
    importedRows.push(nextItem);
    added += 1;
  });

  return {
    rows: importedRows,
    summary: createImportSummary('creator', args.fileName, added, duplicates, errors),
  };
}

export function importPostsFromCsv(args: {
  text: string;
  fileName: string;
  existingRows: CampaignDraftPostItem[];
  availableSlots: number;
  itemLimit: number;
}): PostImportResult {
  const csvRows = parseCsvRows(args.text);
  if (csvRows.length === 0) {
    return {
      rows: args.existingRows,
      summary: createImportSummary('post', args.fileName, 0, 0, [
        { row: 1, message: 'CSV is empty.' },
      ]),
    };
  }

  const [firstRow, ...remainingRows] = csvRows;
  const headerIndexMap = detectHeaderIndexMap(firstRow, POST_HEADERS);
  const hasHeaders = hasKnownHeaders(headerIndexMap);
  const dataRows = hasHeaders ? remainingRows : csvRows;
  const errors: ImportError[] = [];
  const seenKeys = new Set(
    args.existingRows.map((item) => buildPostDedupKey(item)).filter(Boolean),
  );
  const importedRows = [...args.existingRows];
  let duplicates = 0;
  let added = 0;

  dataRows.forEach((row, rowIndex) => {
    const rowNumber = hasHeaders ? rowIndex + 2 : rowIndex + 1;
    const postUrl = hasHeaders
      ? getCell(row, headerIndexMap, 'postUrl')
      : normalizeText(row[0] ?? '');

    const nextPostItem = buildCampaignTrackPostItem(postUrl);
    if (!nextPostItem) {
      errors.push({
        row: rowNumber,
        message: 'Use a supported Instagram, TikTok, or YouTube post URL.',
      });
      return;
    }

    if (!nextPostItem.platform || !CAMPAIGN_SUPPORTED_PLATFORMS.includes(nextPostItem.platform)) {
      errors.push({
        row: rowNumber,
        message: CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
      });
      return;
    }

    const nextItem: CampaignDraftPostItem = {
      id: crypto.randomUUID(),
      postUrl: nextPostItem.post_url,
    };

    const dedupKey = buildPostDedupKey(nextItem);
    if (!dedupKey) {
      errors.push({
        row: rowNumber,
        message: 'Use a supported Instagram, TikTok, or YouTube post URL.',
      });
      return;
    }

    if (seenKeys.has(dedupKey)) {
      duplicates += 1;
      return;
    }

    if (added >= args.availableSlots) {
      errors.push({
        row: rowNumber,
        message: `This row exceeds the ${args.itemLimit} item campaign limit.`,
      });
      return;
    }

    seenKeys.add(dedupKey);
    importedRows.push(nextItem);
    added += 1;
  });

  return {
    rows: importedRows,
    summary: createImportSummary('post', args.fileName, added, duplicates, errors),
  };
}
